import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X } from 'lucide-react';

/**
 * Combobox - Select con búsqueda/filtrado
 */
export default function Combobox({
  options = [],
  value,
  onChange,
  placeholder = 'Seleccionar...',
  disabled = false,
  className = '',
  searchPlaceholder = 'Buscar...',
  emptyMessage = 'No se encontraron resultados',
  actions = [],
  onActionSelect,
  renderInPortal = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const isSelectingRef = useRef(false);
  const inputRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState({});

  const selectedOption = options.find(opt => String(opt.value) === String(value));
  const selectedLabel = selectedOption?.label || '';

  const filteredOptions = options.filter(opt =>
    opt.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideContainer = containerRef.current?.contains(event.target);
      const clickedInsideMenu = menuRef.current?.contains(event.target);
      if (!clickedInsideContainer && !clickedInsideMenu) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus({ preventScroll: true });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !renderInPortal || !containerRef.current) return;

    const updatePosition = () => {
      const rect = containerRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 70,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, renderInPortal]);

  const closeAndReset = () => {
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleSelect = (optionValue) => {
    isSelectingRef.current = true;
    onChange(optionValue);
    closeAndReset();
    setTimeout(() => {
      isSelectingRef.current = false;
    }, 0);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeAndReset();
    }
  };

  const hasActions = actions.length > 0 && typeof onActionSelect === 'function';

  const dropdown = (
    <div
      ref={menuRef}
      className={`z-[9999] pointer-events-auto mt-1 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgb(var(--panel))] shadow-[0_8px_24px_rgba(var(--shadow),0.15)] ${renderInPortal ? 'mt-0' : 'absolute'}`}
      style={renderInPortal ? { ...menuStyle, zIndex: 9999 } : undefined}
    >
      <div className="border-b border-[rgba(var(--border),0.4)] p-2">
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            setTimeout(() => {
              if (!isSelectingRef.current) {
                closeAndReset();
              }
            }, 120);
          }}
          placeholder={searchPlaceholder}
          className="w-full rounded-lg border border-[rgba(var(--border),0.5)] bg-[rgba(var(--subpanel),0.5)] px-3 py-1.5 text-sm text-[rgb(var(--fg))] placeholder:text-[rgb(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
        />
      </div>

      <div className="max-h-60 overflow-y-auto p-1">
        {filteredOptions.length === 0 ? (
          <div className="px-3 py-2 text-center text-sm text-[rgb(var(--muted))]">{emptyMessage}</div>
        ) : (
          filteredOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(option.value);
              }}
              className={`
                w-full rounded-lg px-3 py-2 text-left text-sm transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]
                ${String(option.value) === String(value)
                  ? 'bg-[rgba(var(--brand),0.12)] font-semibold text-[rgb(var(--brand))]'
                  : 'text-[rgb(var(--fg))] hover:bg-[rgba(var(--subpanel),0.7)]'
                }
              `}
            >
              {option.label}
            </button>
          ))
        )}

        {hasActions ? (
          <>
            <div className="my-1 border-t border-[rgba(var(--border),0.4)]" />
            {actions.map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => {
                  onActionSelect(action.id);
                  closeAndReset();
                }}
                className="w-full rounded-lg px-3 py-2 text-left text-sm text-[rgb(var(--fg))] hover:bg-[rgba(var(--subpanel),0.7)]"
              >
                {action.label}
              </button>
            ))}
          </>
        ) : null}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex w-full items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-sm
          transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]
          ${disabled
            ? 'cursor-not-allowed opacity-50 bg-[rgba(var(--panel),0.5)]'
            : 'cursor-pointer hover:bg-[rgba(var(--subpanel),0.6)]'
          }
          ${isOpen
            ? 'border-[rgb(var(--brand))] bg-[rgba(var(--subpanel),0.7)]'
            : 'border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.8)]'
          }
        `}
      >
        <span className={selectedLabel ? 'text-[rgb(var(--fg))]' : 'text-[rgb(var(--muted))]'}>
          {selectedLabel || placeholder}
        </span>
        <div className="flex items-center gap-1">
          {value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="rounded p-0.5 hover:bg-[rgba(var(--subpanel),0.8)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[rgb(var(--ring))]"
            >
              <X size={14} className="text-[rgb(var(--muted))]" />
            </button>
          )}
          <ChevronDown
            size={16}
            className={`text-[rgb(var(--muted))] transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {isOpen && !disabled && (renderInPortal ? createPortal(dropdown, document.body) : dropdown)}
    </div>
  );
}
